import { useMemo, useState } from 'react';
import { Box, Button, Chip, Collapse, Divider, Paper, Stack, Tooltip, Typography } from '@mui/material';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import DirectionsWalkRoundedIcon from '@mui/icons-material/DirectionsWalkRounded';
import BedtimeRoundedIcon from '@mui/icons-material/BedtimeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import WatchRoundedIcon from '@mui/icons-material/WatchRounded';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts';

const RANGE_OPTIONS = [
    { key: '24h', label: '24h' },
    { key: '7d', label: '7 dias' },
    { key: '30d', label: '30 dias' }
];

const cardSx = {
    p: { xs: 2, md: 2.4 },
    borderRadius: '22px',
    bgcolor: '#FFFFFF',
    boxShadow: 'none',
    height: '100%'
};

const chartWrapSx = {
    width: '100%',
    height: 220,
    mt: 1
};

const emptyStateSx = {
    height: 220,
    borderRadius: '18px',
    border: '1px dashed #D9D9D9',
    bgcolor: '#FBFBFB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    px: 2
};

const softStatSx = {
    px: 1.25,
    py: 0.7,
    borderRadius: '999px',
    bgcolor: '#F7F7F7',
    color: '#5F6B7A',
    fontWeight: 700,
    fontSize: '0.8rem'
};

const emptyWearableData = {
    heartRate: [],
    oxygen: [],
    activity: [],
    sleep: {
        duration: '--',
        efficiency: '--',
        wakeups: '--',
        bedtime: '--',
        wakeTime: '--',
        deepSleep: '--',
        remSleep: '--',
        stages: []
    }
};

const SummaryValue = ({ value, suffix, color = '#3D3D3D' }) => (
    <Stack direction="row" spacing={0.75} alignItems="baseline">
        <Typography variant="h3" fontWeight={800} color={color} lineHeight={1}>
            {value}
        </Typography>
        {suffix ? (
            <Typography variant="h6" fontWeight={700} color="#6F6F6F">
                {suffix}
            </Typography>
        ) : null}
    </Stack>
);

const MiniLegend = ({ items }) => (
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
        {items.map((item) => (
            <Stack key={item.label} direction="row" spacing={0.75} alignItems="center">
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '999px',
                        bgcolor: item.color
                    }}
                />
                <Typography variant="caption" color="#6F6F6F" fontWeight={600}>
                    {item.label}
                </Typography>
            </Stack>
        ))}
    </Stack>
);

const CustomTooltip = ({ active, payload, label, formatterSuffix = '' }) => {
    if (!active || !payload?.length) {
        return null;
    }

    return (
        <Paper
            sx={{
                p: 1.1,
                borderRadius: '12px',
                border: '1px solid #E5E5E5',
                boxShadow: '0px 8px 22px rgba(61, 61, 61, 0.08)'
            }}
        >
            <Typography variant="caption" color="#6F6F6F" fontWeight={700}>
                {label}
            </Typography>
            {payload.map((entry) => (
                <Typography
                    key={`${entry.dataKey}-${entry.value}`}
                    variant="body2"
                    fontWeight={700}
                    color="#3D3D3D"
                >
                    {entry.name}: {entry.value}
                    {formatterSuffix}
                </Typography>
            ))}
        </Paper>
    );
};

const EmptyCardState = ({ message = 'Sem dados adquiridos.' }) => (
    <Box sx={emptyStateSx}>
        <Typography variant="body2" color="#8A8A8A" fontWeight={700}>
            {message}
        </Typography>
    </Box>
);

const SleepStageStrip = ({ stages = [] }) => (
    <Stack spacing={1.25}>
        <Stack direction="row" sx={{ width: '100%', borderRadius: '999px', overflow: 'hidden', height: 18 }}>
            {stages.map((stage, index) => (
                <Box
                    key={`${stage.title}-${index}`}
                    title={stage.title}
                    sx={{
                        width: `${stage.width}%`,
                        bgcolor: stage.color,
                        borderRight: index < stages.length - 1 ? '2px solid #FFFFFF' : 'none'
                    }}
                />
            ))}
        </Stack>
        <MiniLegend
            items={[
                { label: 'Leve', color: '#D8E5FF' },
                { label: 'Profundo', color: '#9CB8FF' },
                { label: 'REM', color: '#F8E07A' },
                { label: 'Desperto', color: '#F5D5D2' }
            ]}
        />
    </Stack>
);

const SleepMetricPill = ({ label, value }) => (
    <Box
        sx={{
            p: 1.3,
            borderRadius: '16px',
            bgcolor: '#F7F7F7',
            minWidth: 0
        }}
    >
        <Typography variant="caption" color="#8A8A8A" fontWeight={700}>
            {label}
        </Typography>
        <Typography variant="body1" color="#3D3D3D" fontWeight={800} mt={0.35}>
            {value}
        </Typography>
    </Box>
);

const getOxygenStatus = (value) => {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
        return {
            label: 'Sem dados',
            color: '#6F6F6F',
            background: '#F7F7F7'
        };
    }

    if (numericValue >= 95) {
        return {
            label: 'Normal',
            color: '#2E7D32',
            background: 'rgba(46, 125, 50, 0.10)'
        };
    }

    if (numericValue >= 92) {
        return {
            label: 'Atencao',
            color: '#B26A00',
            background: 'rgba(255, 179, 0, 0.16)'
        };
    }

    return {
        label: 'Baixa',
        color: '#D63731',
        background: 'rgba(214, 55, 49, 0.10)'
    };
};

const HeartRateCard = ({ data }) => {
    const safeData = Array.isArray(data) ? data : [];
    const hasData = safeData.length > 0;
    const latest = hasData ? safeData[safeData.length - 1]?.value || '--' : '--';
    const resting = hasData ? Math.min(...safeData.map((item) => Number(item.value) || 0)) : '--';

    return (
        <Paper sx={cardSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Stack spacing={0.6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '14px',
                                bgcolor: 'rgba(214, 55, 49, 0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#D63731'
                            }}
                        >
                            <FavoriteRoundedIcon />
                        </Box>
                        <Typography variant="h6" fontWeight={800} color="#3D3D3D">
                            Ritmo cardiaco
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="#7A7A7A">
                        Tendencia cardiaca ao longo do periodo selecionado.
                    </Typography>
                </Stack>
                <Chip
                    label={hasData ? 'Estavel' : 'Sem dados'}
                    sx={{ ...softStatSx, color: '#D63731', bgcolor: 'rgba(214, 55, 49, 0.08)' }}
                />
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1.2}>
                <SummaryValue value={latest} suffix="bpm" color="#D63731" />
                <Typography variant="body2" color="#6F6F6F" fontWeight={700}>
                    {hasData ? `repouso ${resting} bpm` : 'Sem dados adquiridos'}
                </Typography>
            </Stack>

            {hasData ? (
                <Box sx={chartWrapSx}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={safeData} margin={{ top: 10, right: 12, left: 4, bottom: 0 }}>
                            <CartesianGrid stroke="#EFEFEF" strokeDasharray="4 4" vertical={false} />
                            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#8A8A8A', fontSize: 12 }} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#8A8A8A', fontSize: 12 }} width={42} />
                            <RechartsTooltip content={<CustomTooltip formatterSuffix=" bpm" />} />
                            <Line
                                type="monotone"
                                dataKey="value"
                                name="FC"
                                stroke="#D63731"
                                strokeWidth={3}
                                dot={{ r: 3, strokeWidth: 0, fill: '#D63731' }}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            ) : (
                <EmptyCardState />
            )}
        </Paper>
    );
};

const OxygenCard = ({ data }) => {
    const safeData = Array.isArray(data) ? data : [];
    const hasData = safeData.length > 0;
    const latestEntry = safeData[safeData.length - 1] || null;
    const latest = latestEntry?.value ?? '--';
    const latestLabel = latestEntry?.label || '--';
    const recentEntries = safeData.slice(-4).reverse();
    const hasTrend = safeData.length >= 3;
    const status = getOxygenStatus(latest);

    return (
        <Paper sx={cardSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Stack spacing={0.6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '14px',
                                bgcolor: '#F7F7F7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#6F6F6F'
                            }}
                        >
                            <AirRoundedIcon />
                        </Box>
                        <Typography variant="h6" fontWeight={800} color="#3D3D3D">
                            Saturacao de Oxigenio
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="#7A7A7A">
                        Leitura pontual de SpO2 com foco no ultimo valor e no historico recente.
                    </Typography>
                </Stack>
                <Chip
                    label={status.label}
                    sx={{
                        ...softStatSx,
                        color: status.color,
                        bgcolor: status.background
                    }}
                />
            </Stack>

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                spacing={2}
                mb={2}
            >
                <Stack spacing={0.9}>
                    <SummaryValue value={latest} suffix="%" />
                    <Typography variant="body2" color="#6F6F6F" fontWeight={700}>
                        Ultima medicao: {latestLabel}
                    </Typography>
                </Stack>

                {hasTrend ? (
                    <Box
                        sx={{
                            width: { xs: '100%', md: 170 },
                            height: 72,
                            borderRadius: '18px',
                            bgcolor: '#FBFBFB',
                            border: '1px solid #F0F0F0',
                            px: 1,
                            py: 0.75
                        }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={safeData} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                                <RechartsTooltip content={<CustomTooltip formatterSuffix="%" />} />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    name="SpO2"
                                    stroke="#D63731"
                                    strokeWidth={2.5}
                                    dot={false}
                                    activeDot={{ r: 4, fill: '#D63731' }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                ) : null}
            </Stack>

            {hasData ? (
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '18px',
                        bgcolor: '#FBFBFB',
                        border: '1px solid #F0F0F0'
                    }}
                >
                    <Typography variant="body2" color="#6F6F6F" fontWeight={700} mb={1.25}>
                        Historico recente
                    </Typography>

                    <Stack spacing={1}>
                        {recentEntries.map((entry, index) => (
                            <Stack
                                key={`${entry.label}-${index}`}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: '14px',
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #F1F1F1'
                                }}
                            >
                                <Typography variant="body2" color="#6F6F6F" fontWeight={700}>
                                    {entry.label}
                                </Typography>
                                <Typography variant="body1" color="#3D3D3D" fontWeight={800}>
                                    {entry.value}%
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            ) : (
                <EmptyCardState message="Sem medicoes recentes de SpO2. Esta leitura depende de ativacao manual no wearable." />
            )}
        </Paper>
    );
};

const ActivityCard = ({ data }) => {
    const safeData = Array.isArray(data) ? data : [];
    const hasData = safeData.length > 0;
    const totalSteps = safeData.reduce((sum, item) => sum + (Number(item.steps) || 0), 0);
    const totalActive = safeData.reduce((sum, item) => sum + (Number(item.active) || 0), 0);

    return (
        <Paper sx={cardSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Stack spacing={0.6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '14px',
                                bgcolor: 'rgba(214, 55, 49, 0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#D63731'
                            }}
                        >
                            <DirectionsWalkRoundedIcon />
                        </Box>
                        <Typography variant="h6" fontWeight={800} color="#3D3D3D">
                            Passos e Atividade
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="#7A7A7A">
                        Movimentos acumulados e tempo de atividade registado.
                    </Typography>
                </Stack>
                <Chip
                    label={hasData ? 'Boa adesao' : 'Sem dados'}
                    sx={{ ...softStatSx, color: '#D63731', bgcolor: 'rgba(214, 55, 49, 0.08)' }}
                />
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1.2}>
                <SummaryValue value={totalSteps.toLocaleString('pt-PT')} suffix="passos" />
                <Typography variant="body2" color="#6F6F6F" fontWeight={700}>
                    {hasData ? `${totalActive} min ativos` : 'Sem dados adquiridos'}
                </Typography>
            </Stack>

            {hasData ? (
                <Box sx={chartWrapSx}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={safeData} margin={{ top: 10, right: 12, left: 2, bottom: 0 }} barGap={8}>
                            <CartesianGrid stroke="#EFEFEF" strokeDasharray="4 4" vertical={false} />
                            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#8A8A8A', fontSize: 12 }} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#8A8A8A', fontSize: 12 }} width={46} />
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Bar dataKey="steps" name="Passos" fill="#D63731" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            ) : (
                <EmptyCardState />
            )}
        </Paper>
    );
};

const SleepCard = ({ sleep }) => {
    const safeSleep = sleep || emptyWearableData.sleep;
    const hasStages = Array.isArray(safeSleep.stages) && safeSleep.stages.length > 0;
    const hasSummary = [safeSleep.duration, safeSleep.efficiency, safeSleep.deepSleep, safeSleep.remSleep].some(
        (value) => value && value !== '--'
    );

    return (
        <Paper sx={cardSx}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Stack spacing={0.6}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '14px',
                            bgcolor: '#F7F7F7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6F6F6F'
                        }}
                    >
                        <BedtimeRoundedIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={800} color="#3D3D3D">
                        Qualidade de Sono
                    </Typography>
                </Stack>
                <Typography variant="body2" color="#7A7A7A">
                    Leitura resumida da noite com fases, consistencia e recuperacao.
                </Typography>
            </Stack>
        </Stack>

        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            justifyContent="flex-start"
            mb={2.25}
        >
            <Stack spacing={1.1}>
                <SummaryValue value={safeSleep.duration} />
                <Typography variant="body2" color="#6F6F6F" fontWeight={700}>
                    {safeSleep.bedtime} - {safeSleep.wakeTime}
                </Typography>
            </Stack>
        </Stack>

        {hasSummary || hasStages ? (
            <>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                        gap: 1.2,
                        mb: 2.25
                    }}
                >
                    <SleepMetricPill label="Eficiencia" value={safeSleep.efficiency} />
                    <SleepMetricPill label="Despertares" value={String(safeSleep.wakeups)} />
                    <SleepMetricPill label="Sono profundo" value={safeSleep.deepSleep} />
                    <SleepMetricPill label="Sono REM" value={safeSleep.remSleep} />
                </Box>

                {hasStages ? (
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: '18px',
                            bgcolor: '#FBFBFB',
                            border: '1px solid #F0F0F0'
                        }}
                    >
                        <Typography variant="body2" color="#6F6F6F" fontWeight={700} mb={1.2}>
                            Arquitetura do sono
                        </Typography>
                        <SleepStageStrip stages={safeSleep.stages} />
                    </Box>
                ) : (
                    <EmptyCardState />
                )}
            </>
        ) : (
            <EmptyCardState />
        )}
    </Paper>
    );
};

const CaregiverWearableSection = ({
    patientName,
    wearableDevice,
    loadingWearableDevice = false,
    wearableRecords = [],
    loadingWearableRecords = false
}) => {
    const [selectedRange, setSelectedRange] = useState('24h');
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const data = useMemo(() => emptyWearableData, [selectedRange]);
    const hasWearable = Boolean(wearableDevice?.uuid);
    const latestRecord = Array.isArray(wearableRecords) && wearableRecords.length > 0 ? wearableRecords[0] : null;

    const formatDateTime = (value) => {
        if (!value) {
            return '--';
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }

        return parsed.toLocaleString('pt-PT');
    };

    return (
        <Stack spacing={2.5}>
            <Paper
                sx={{
                    ...cardSx,
                    p: { xs: 2, md: 2.6 }
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h5" fontWeight={800} color="#3D3D3D">
                            Wearable
                        </Typography>
                        <Typography variant="body2" color="#7A7A7A" mt={0.6}>
                            Visao resumida dos dados automaticos do wearable de {patientName || 'Paciente'}.
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {RANGE_OPTIONS.map((option) => {
                                const selected = option.key === selectedRange;

                                return (
                                    <Chip
                                        key={option.key}
                                        label={option.label}
                                        clickable
                                        onClick={() => setSelectedRange(option.key)}
                                        sx={{
                                            minHeight: 38,
                                            px: 1.1,
                                            borderRadius: '999px',
                                            fontWeight: 800,
                                            color: selected ? '#FFFFFF' : '#3D3D3D',
                                            bgcolor: selected ? '#D63731' : '#FFFFFF',
                                            border: selected ? '1px solid #D63731' : '1px solid #D9D9D9',
                                            '&:hover': {
                                                bgcolor: selected ? '#D63731' : '#F7F7F7'
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                        {hasWearable ? (
                            <Tooltip title="Definicoes do wearable">
                                <Button
                                    variant="outlined"
                                    onClick={() => setSettingsExpanded((prev) => !prev)}
                                    endIcon={settingsExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                                    sx={{
                                        minHeight: 40,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        color: '#3D3D3D',
                                        borderColor: '#D9D9D9',
                                        borderRadius: '999px',
                                        px: 1.8
                                    }}
                                >
                                    Definicoes do wearable
                                </Button>
                            </Tooltip>
                        ) : null}
                    </Stack>
                </Stack>

                {hasWearable ? (
                    <Collapse in={settingsExpanded}>
                        <Paper
                            sx={{
                                mt: 2.4,
                                p: 2,
                                borderRadius: '18px',
                                bgcolor: '#FAFAFA',
                                border: '1px solid #E7E7E7',
                                boxShadow: 'none'
                            }}
                        >
                            <Stack spacing={1.25}>
                                <Typography variant="h6" fontWeight={800} color="#3D3D3D">
                                    Dados do wearable
                                </Typography>
                                <Divider />
                                <Stack
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                        gap: 1.5
                                    }}
                                >
                                    <Typography variant="body2"><strong>Nome:</strong> {wearableDevice?.name || '--'}</Typography>
                                    <Typography variant="body2"><strong>Modelo:</strong> {wearableDevice?.model || '--'}</Typography>
                                    <Typography variant="body2"><strong>Tipo:</strong> {wearableDevice?.type || '--'}</Typography>
                                    <Typography variant="body2"><strong>Associado a:</strong> {wearableDevice?.role?.name || '--'}</Typography>
                                    <Typography variant="body2"><strong>UUID:</strong> {wearableDevice?.uuid || '--'}</Typography>
                                    <Typography variant="body2"><strong>Parametros:</strong> {wearableDevice?.parameters ? JSON.stringify(wearableDevice.parameters) : '--'}</Typography>
                                    <Typography variant="body2"><strong>Ultimo registo:</strong> {formatDateTime(latestRecord?.ts)}</Typography>
                                    <Typography variant="body2"><strong>Tipo do ultimo registo:</strong> {latestRecord?.dtype || '--'}</Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Collapse>
                ) : null}
                <Box mt={2.4}>
                    {loadingWearableDevice ? (
                        <Paper sx={{ ...cardSx, textAlign: 'center', py: 3 }}>
                            <Typography color="#6F6F6F" fontWeight={700}>
                                A carregar dados do wearable...
                            </Typography>
                        </Paper>
                    ) : !hasWearable ? (
                        <Paper
                            sx={{
                                p: 2,
                                borderRadius: '18px',
                                bgcolor: 'rgba(214, 55, 49, 0.06)',
                                border: '1px solid rgba(214, 55, 49, 0.18)',
                                boxShadow: 'none'
                            }}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: '14px',
                                        bgcolor: 'rgba(214, 55, 49, 0.12)',
                                        color: '#D63731',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}
                                >
                                    <WatchRoundedIcon />
                                </Box>
                                <Box>
                                    <Typography variant="body1" fontWeight={800} color="#3D3D3D">
                                        Sem wearable associado
                                    </Typography>
                                    <Typography variant="body2" color="#7A7A7A">
                                        O cuidador pode associar um dispositivo wearable diretamente na aplicacao movel.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    ) : null}
                </Box>
            </Paper>

            <Paper sx={{ ...cardSx, py: 2 }}>
                <Typography variant="body2" color="#6F6F6F" fontWeight={700}>
                    {loadingWearableRecords
                        ? 'A carregar registos do wearable...'
                        : `Registos recebidos do backend: ${wearableRecords.length}`}
                </Typography>
                {latestRecord ? (
                    <Typography variant="caption" color="#8A8A8A" display="block" mt={0.6}>
                        Ultimo registo: {formatDateTime(latestRecord.ts)} | Tipo: {latestRecord.dtype || '--'}
                    </Typography>
                ) : null}
            </Paper>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
                    gap: 2.5
                }}
            >
                <HeartRateCard data={data.heartRate} />
                <ActivityCard data={data.activity} />
                <OxygenCard data={data.oxygen} />
                <SleepCard sleep={data.sleep} />
            </Box>
        </Stack>
    );
};

export default CaregiverWearableSection;


